import React from 'react';
import styled from 'styled-components';
import posthog from 'posthog-js';
import { utils } from '@makeship/core';
import Link from 'next/link';
import { Caption, CaptionStyles, S3Styles } from '../Typography';
import config from '../../../config.json';
import { ExperimentVariant, getExperimentFlags } from '../../context/flagging';
import SidebarAccordion from '../pages/collection/SidebarAccordion';
import { isUserLoggedIn } from '../../utils/analytics';
import { useStore } from '../../store';
import { experimentKeys } from '../../utils/flagging';

const { collections, plushCollectionHandles, apparelCollectionHandles, topPicksCollectionHandles } = config;

const SidebarWrapper = styled.div<{ show?: boolean }>`
  width: 200px;
  ${({ show }) => !show && `opacity: 0; margin-left: -200px; visibility: hidden;`};
  position: -webkit-sticky;
  position: sticky;
  top: ${({ theme }) => theme.bannerHeight + theme.headerHeight + 32}px;
  align-self: flex-start;
  white-space: nowrap;
  transition: all 0.25s ease-in-out;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    position: relative;
    top: 0;
    width: 100%;
    opacity: 1;
    margin-left: 0;
    visibility: visible;
  }
`;

const SidebarSection = styled.div<{ mobile?: boolean; isCollectionsSidebarExperimentEnabled?: boolean }>`
  margin-bottom: ${({ isCollectionsSidebarExperimentEnabled }) =>
    isCollectionsSidebarExperimentEnabled ? '0' : '32px'};
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    width: calc(100% + 32px);
    margin-left: -16px;
    padding-left: 16px;
    overflow-x: scroll;
    -ms-overflow-style: none;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 0;
      height: 0;
    }
    display: ${({ mobile }) => (mobile ? 'flex' : 'none')};
  }
`;

const SidebarHeading = styled(Caption)`
  color: ${({ theme }) => utils.hexToRGBA(theme.colors.neutral7, theme.alpha.dark)};
  text-transform: uppercase;
  margin-bottom: 24px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    display: none;
  }
`;

const SidebarItem = styled(Caption)<{
  active?: boolean;
  last?: boolean;
}>`
  ${CaptionStyles}
  display:block;
  margin: 8px 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }

  ${({ active }) => active && S3Styles}

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    margin-right: ${({ last }) => (last ? 16 : 24)}px;
  }
`;

const NewCollectionText = styled.span<{ active?: boolean }>`
  ${CaptionStyles}
  color: ${({ theme }) => theme.colors.greenNewTag};
  display: inline;
  font-weight: ${({ active }) => (active ? 'bold' : 'normal')};
`;

type SidebarProps = {
  handle?: string;
  show?: boolean;
  sidebarItems: SidebarItemsType[];
};

const Sidebar: React.FC<SidebarProps> = ({ handle, show, sidebarItems }: SidebarProps) => {
  const { state } = useStore();

  const experimentFlags = getExperimentFlags();
  const isCollectionsSidebarExperimentEnabled =
    experimentFlags[experimentKeys.collectionsSidebarExperiment] === ExperimentVariant.Variant1;

  // Filters out subcategories, like jumbos, hoodies, longbois, etc.
  const sidebarItemsWithoutSubcategories = sidebarItems.map((item) => {
    const links = item.links.filter(
      (link) =>
        !Object.values(plushCollectionHandles).includes(link.handle || '') &&
        !Object.values(apparelCollectionHandles).includes(link.handle || '') &&
        !Object.values(topPicksCollectionHandles).includes(link.handle || ''),
    );

    return {
      ...item,
      links,
    };
  });

  const renderSidebarLink = (link: SidebarItemType, index: number, sidebarItem: SidebarItemsType) => (
    <Link key={link.title} href={`/shop/${link.handle}`} passHref legacyBehavior>
      <SidebarItem active={link.handle === handle} last={sidebarItem.links.length - 1 === index}>
        {link.isNew && (
          <NewCollectionText data-gtm-name="span-new" active={link.handle === handle}>
            New!{' '}
          </NewCollectionText>
        )}
        {link.title}
      </SidebarItem>
    </Link>
  );

  // Renders the experiment variant of the sidebar
  // Used to insert accordion links into the sidebar at specific locations
  const renderVariantSidebarItem = (sidebarItem: SidebarItemsType) =>
    sidebarItemsWithoutSubcategories.map((noSubcategoriesSidebarItem) =>
      noSubcategoriesSidebarItem.links.map((link, index) => {
        if (link.handle === collections.liveCampaigns) {
          return (
            <>
              {renderSidebarLink(link, index, sidebarItem)}
              <SidebarAccordion
                sidebarItems={sidebarItem.links}
                sidebarProductTypeHandle={plushCollectionHandles.plushies}
                sidebarProductTypeTitle="Plushies"
                handle={handle}
              />
              <SidebarAccordion
                sidebarItems={sidebarItem.links}
                sidebarProductTypeHandle={apparelCollectionHandles.apparel}
                sidebarProductTypeTitle="Apparel"
                handle={handle}
              />
            </>
          );
        }
        if (link.handle === collections.petitions) {
          return (
            <>
              {renderSidebarLink(link, index, sidebarItem)}
              <SidebarAccordion
                sidebarItems={sidebarItem.links}
                sidebarProductTypeHandle={topPicksCollectionHandles.topSellers}
                sidebarProductTypeTitle="Top Picks"
                handle={handle}
              />
            </>
          );
        }
        return renderSidebarLink(link, index, sidebarItem);
      }),
    );

  return (
    <SidebarWrapper show={show}>
      {sidebarItems.map((sidebarItem) => (
        <SidebarSection
          data-gtm-name="filter-name"
          key={sidebarItem.title}
          mobile={sidebarItem.mobile}
          isCollectionsSidebarExperimentEnabled={isCollectionsSidebarExperimentEnabled}
        >
          <SidebarHeading>{sidebarItem.title}</SidebarHeading>
          {isCollectionsSidebarExperimentEnabled ? (
            <>{renderVariantSidebarItem(sidebarItem)}</>
          ) : (
            sidebarItem.links.map((link, index) => (
              <Link key={link.title} href={`/shop/${link.handle}`} passHref legacyBehavior>
                <SidebarItem
                  active={link.handle === handle}
                  last={sidebarItem.links.length - 1 === index}
                  onClick={() => {
                    posthog.capture('collection_sidebar_item_click', {
                      itemTitle: link.title,
                      itemHandle: link.handle,
                      is_logged_in: isUserLoggedIn(state.user),
                    });
                  }}
                >
                  {link.isNew && (
                    <NewCollectionText data-gtm-name="span-new" active={link.handle === handle}>
                      New!{' '}
                    </NewCollectionText>
                  )}
                  {link.title}
                </SidebarItem>
              </Link>
            ))
          )}
        </SidebarSection>
      ))}
    </SidebarWrapper>
  );
};

export default Sidebar;
